import { useMeasurementsHistoryPresets, useMeasurementsOverview } from "@api/generated/measurements/measurements";
import type { SelectionDraft, SelectionIn, SelectionPreview, SelectionRun, SourceList, TargetPlot } from "@api/generated/model";
import {
  selectionSources,
  useSelectionNames,
  useSelectionPreview,
  useSelectionRun,
  useSelectionTarget,
} from "@api/generated/selection/selection";
import { useUserMe } from "@api/generated/user/user";
import { KEEP } from "@app/trials";
import { hasRole, useAutosave, useCreateSeason, useSeasonFromLink, useSettings } from "@app/workspace";
import { readableFields } from "@features/trials/fields";
import "@features/trials/formulas.css";
import {
  PreviewStep,
  RemovalModal,
  RulesStep,
  RulesSummary,
  SourceStep,
  SourceTable,
  SubplotModal,
  TargetPanel,
  useSourceTable,
} from "@features/trials/SelectionParts";
import "@features/trials/selection.css";
import { useSavedQuery } from "@features/queries/SavedQuerySelect";
import { ResultBanner } from "@features/system/undo";
import {
  keyOf,
  lineOf,
  matching,
  pickOf,
  picksOf,
  plotOf,
  range,
  ready,
  rowOf,
  type SelectionSpec,
  sistersOf,
  sourceName,
  sourceSites,
  sourceText,
  specOf,
  toggled,
} from "@features/trials/selection";
import { Button, Loader } from "@mantine/core";
import { useDebouncedValue, useHotkeys } from "@mantine/hooks";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { formatNumber, shortTime } from "@ui/format";
import { moved } from "@ui/list";
import { PageHeader } from "@ui/PageHeader";
import { added } from "@ui/picks";
import { QueryFeedback } from "@ui/QueryFeedback";
import { Steps } from "@ui/Steps";
import { type KeyboardEvent, useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

export const Route = createFileRoute("/season/selection")({
  validateSearch: z.object({ ev: z.number().int().optional(), kis: z.string().optional() }),
  component: SelectionPage,
});

const STEPS = ["source", "rules", "choose", "preview", "commit"] as const;

/** Tenyészidőszak → Szelekció (the legacy Selection): plots of the working season (of trials, of a saved query, or the
 *  sisters of trials' lines) go into a trial of the next year with their lineage, code and spike type, the seed they
 *  take is debited from the source plots' ledger. The draft (source, rules and chosen plots) is kept with the user's
 *  settings; the commit is one undoable operation. */
function SelectionPage() {
  const { t } = useTranslation();
  const canSelect = hasRole(useUserMe().data?.roles, "nemesito");
  const search = Route.useSearch();
  const ev = useSeasonFromLink(search.ev);
  const settings = useSettings();
  if (!canSelect) {
    return (
      <div className="page">
        <PageHeader title={t("selection.title")} />
        <p className="mu">{t("selection.breedersOnly")}</p>
      </div>
    );
  }
  if (!settings || !ev) return <Loader size="sm" m="md" />;
  return (
    <SelectionWizard key={ev} ev={ev} stored={settings.selection_draft ?? null} linked={search.kis} home={settings.default_th ?? "MV"} />
  );
}

/** ``linked``: a trial the link names (a trial page's "Szelekció"): it becomes the source and the wizard starts at step 1.
 *  ``home``: the user's default site, offered for trials measured nowhere yet. */
function SelectionWizard({ ev, stored, linked, home }: { ev: number; stored: SelectionDraft | null; linked?: string; home: string }) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const navigate = useNavigate();
  // the stored draft continues where the user left off when it was made in this season
  const [init] = useState(() => (stored?.ev === ev ? stored : null));
  const initial = useMemo(() => specOf(init), [init]);
  const fresh = !!linked && (initial.source !== "trials" || initial.trials.join() !== linked);
  const [spec, setSpec] = useState<SelectionSpec>(() => (fresh && linked ? { ...initial, source: "trials", trials: [linked] } : initial));
  const [picks, setPicks] = useState<string[]>(fresh ? [] : picksOf(init));
  const [step, setStep] = useState(!fresh && init?.step && init.step < 5 ? init.step : 1);
  const draft = useMemo<SelectionDraft>(
    () => ({ ev, kis: spec.trials[0] ?? null, step, spec: spec as never, picks }),
    [ev, step, spec, picks],
  );
  const { savedAt } = useAutosave("selection_draft", draft, init); // another season's draft goes once something changes

  const overview = useMeasurementsOverview(ev, { query: KEEP }).data;
  const trials = useMemo(() => (overview ?? []).filter((o) => o.register_plots > 0), [overview]);
  // the site: one of the chosen trials' (for a saved query: any trial's of the season), home for unmeasured ones
  const sites = useMemo(() => {
    const chosen = spec.source === "query" ? trials : trials.filter((o) => spec.trials.includes(o.kis));
    return sourceSites(chosen, home);
  }, [trials, spec.source, spec.trials, home]);
  const site = spec.rules.site && sites.includes(spec.rules.site) ? spec.rules.site : (sites[0] ?? spec.rules.site ?? "");
  const targetTrials = (useMeasurementsOverview(ev + 1, { query: { staleTime: 60_000 } }).data ?? []).map((o) => o.kis);
  const presets = useMeasurementsHistoryPresets({ query: { staleTime: Number.POSITIVE_INFINITY } }).data?.fields;
  const fieldOptions = useMemo(() => readableFields(presets ?? []), [presets]);
  const names = useSelectionNames({ ev, trials: spec.trials }, { query: { enabled: spec.source === "sisters" && spec.trials.length > 0 } });
  const saved = useSavedQuery(spec.query, spec.source === "query");
  const query = saved.query;
  const source = sourceName(spec, query?.name);

  const target = spec.rules.target.trim();
  const rules = useMemo(() => ({ ...spec.rules, site }), [spec.rules, site]);
  const body = useMemo<SelectionIn | null>(() => (ready({ ...spec, rules }) ? { ...spec, ev, rules } : null), [spec, rules, ev]);
  // the list depends only on what it reads (not on the code, rows or numbering of the new plots); a saved query's
  // stamp refreshes it when the query is saved again in the builder
  const listBody = useMemo(
    () =>
      body && {
        ...body,
        rules: { target: body.rules.target, site: body.rules.site, seed: body.rules.seed, copies: body.rules.copies },
      },
    [body],
  );
  const [debounced] = useDebouncedValue(listBody, 400);
  const sources = useQuery({
    queryKey: ["selection-sources", debounced, saved.stamp],
    enabled: !!debounced && step >= 2 && saved.ready,
    queryFn: ({ signal }) => selectionSources(debounced as SelectionIn, { signal }),
    placeholderData: keepPreviousData,
  });
  const list = sources.data;
  const existing = useSelectionTarget({ ev: ev + 1, kis: target }, { query: { enabled: !!target && step >= 3 } }).data ?? [];

  const [preview, setPreview] = useState<SelectionPreview | null>(null);
  const [result, setResult] = useState<SelectionRun | null>(null);
  const season = useCreateSeason(() => {
    // the next season is open now: what the list and the preview said about it changes, nothing else
    client.setQueriesData<SourceList>({ queryKey: ["selection-sources"] }, (d) => d && { ...d, season_exists: true });
    setPreview((p) => p && { ...p, season_exists: true });
  });
  const previewBody = () => ({ ...(body as SelectionIn), picks: picks.map(pickOf) });
  const previewing = useSelectionPreview({ mutation: { onSuccess: setPreview } });
  const running = useSelectionRun({
    mutation: {
      onSuccess: (out) => {
        setResult(out);
        setPicks([]);
        setPreview(null);
        setStep(5);
        void client.invalidateQueries({ queryKey: ["selection-sources"] });
        void client.invalidateQueries({ queryKey: ["measurements"] });
      },
    },
  });
  const change = (next: SelectionSpec) => {
    // another source, other plots: the chosen ones may not be in it; each subplot mode lists other rows
    const listed = (s: SelectionSpec) => [s.source, s.query, s.subplots, ...s.trials, "|", ...(s.exclude ?? [])].join();
    if (listed(next) !== listed(spec)) setPicks([]);
    setSpec(next);
  };

  const goPreview = () => {
    setStep(4);
    previewing.mutate({ data: previewBody() });
  };
  const title = body ? `${ev} · ${source}  →  ${ev + 1} · ${target}` : "";
  return (
    <div className="page fill">
      <PageHeader
        title={t("selection.title")}
        season
        subtitle={title}
        actions={
          <div className="hstack" style={{ gap: 12 }}>
            {savedAt && <span className="sm mu">{t("selection.draftSaved", { time: shortTime(savedAt.toISOString()) })}</span>}
          </div>
        }
      />
      <Steps
        names={STEPS.map((s) => t(`selection.steps.${s}`))}
        step={step}
        onStep={step < 5 ? setStep : undefined}
        details={[
          body ? sourceText(source, site, target) : null,
          step > 2 ? t(`selection.seedMode.${spec.rules.seed.mode}`) : null,
          picks.length ? t("selection.stepPicked", { count: picks.length }) : null,
          preview ? t("selection.stepNewPlots", { count: preview.rows.length }) : null,
          result ? t("selection.stepWritten", { count: result.written }) : null,
        ]}
      />
      {step >= 2 && step <= 3 && <QueryFeedback busy={false} error={sources.error} retry={sources.refetch} />}
      {step === 1 && (
        <SourceStep
          ev={ev}
          spec={spec}
          source={source}
          trials={trials}
          sites={sites}
          site={site}
          names={names.data ?? []}
          targetTrials={targetTrials}
          seasonExists={list?.season_exists}
          creating={season.pending}
          onChange={change}
          onCreateSeason={() => season.create(ev + 1)}
          onNext={() => {
            setSpec({ ...spec, rules: { ...spec.rules, site } });
            setStep(2);
          }}
        />
      )}
      {step === 2 && (
        <RulesStep
          spec={spec}
          fields={fieldOptions}
          targetTrials={targetTrials}
          problems={list?.problems ?? []}
          onChange={change}
          onBack={() => setStep(1)}
          onNext={() => setStep(3)}
        />
      )}
      {step === 3 && (
        <PickStep
          ev={ev}
          source={source}
          spec={spec}
          list={list}
          loading={sources.isFetching}
          picks={picks}
          existing={existing}
          onPicks={setPicks}
          onEdit={() => setStep(2)}
          onPreview={goPreview}
        />
      )}
      {step === 4 &&
        (preview ? (
          <PreviewStep
            ev={ev}
            target={target}
            preview={preview}
            running={running.isPending}
            creating={season.pending}
            onCreateSeason={() => season.create(ev + 1)}
            onBack={() => setStep(3)}
            onRun={() => running.mutate({ data: { ...previewBody(), token: preview.token } })}
          />
        ) : (
          <Loader size="sm" />
        ))}
      {step === 5 && (
        <ResultBanner
          operation={result?.operation}
          actions={
            <>
              <Button size="xs" variant="default" onClick={() => setStep(3)}>
                {t("selection.newSelection")}
              </Button>
              <Button
                size="xs"
                variant="default"
                onClick={() => navigate({ to: "/trials/data", search: { ev: ev + 1, kis: target, table: "alap" } })}
              >
                {t("selection.targetRows")}
              </Button>
            </>
          }
        >
          <b>
            {result?.written
              ? t("selection.done", { count: formatNumber(result.written), ev: ev + 1, kis: target })
              : t("selection.doneNone")}
          </b>
        </ResultBanner>
      )}
    </div>
  );
}

const NO_FIELDS: never[] = [];

/** 3 · Kijelölés: the source list on the left of the target, the rules beside them. With manual subplots "Hozzáadás"
 *  asks for the subplot numbers of the marked plots (the legacy prompt), each a pick of its own. */
function PickStep(props: {
  ev: number;
  source: string;
  spec: SelectionSpec;
  list: SourceList | undefined;
  loading: boolean;
  picks: string[];
  existing: TargetPlot[];
  onPicks: (picks: string[]) => void;
  onEdit: () => void;
  onPreview: () => void;
}) {
  const { list, picks, onPicks } = props;
  const manual = props.spec.subplots === "manual";
  const [filter, setFilter] = useState("");
  const [hideTaken, setHideTaken] = useState(false);
  const [marked, setMarked] = useState<Set<string>>(new Set());
  const [cursor, setCursor] = useState(0);
  const [anchor, setAnchor] = useState(0);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [takeOut, setTakeOut] = useState<number[] | null>(null);
  const [typing, setTyping] = useState<string[] | null>(null); // the plots manual subplot numbers are asked for
  const all = list?.rows ?? [];
  const byKey = useMemo(() => new Map(all.map((r) => [keyOf(r), r])), [all]);
  const picked = useMemo(() => new Set(manual ? picks.map(plotOf) : picks), [picks, manual]);
  const lines = useMemo(() => new Set(picks.map((k) => rowOf(byKey, k)).map((r) => r && lineOf(r))), [picks, byKey]);
  const listed = useMemo(() => {
    const shown = matching(all, filter);
    return hideTaken ? shown.filter((r) => !lines.has(lineOf(r))) : shown;
  }, [all, filter, hideTaken, lines]);
  const several = useMemo(() => new Set(all.map((r) => r.kis)).size > 1, [all]);
  const toggle = useCallback((keys: string[], on?: boolean) => setMarked((m) => toggled(m, keys, on)), []);
  const table = useSourceTable({
    rows: listed,
    fields: list?.fields ?? NO_FIELDS,
    marked,
    picked,
    trials: several,
    sisters: props.spec.source === "sisters",
    onMarkAll: toggle,
  });
  // the rows in the order shown (the person's sort and filters): the cursor, Shift ranges and the picks follow it
  const rows = table.rows;

  // one click handler for every row: it reads the rows shown and the anchor of the last render
  const latest = useRef({ rows, anchor });
  latest.current = { rows, anchor };
  const mark = useCallback(
    (i: number, shift: boolean) => {
      const { rows, anchor } = latest.current;
      const r = rows[i];
      if (!r) return;
      setCursor(i);
      if (shift) toggle(range(rows, anchor, i), true);
      else {
        toggle([keyOf(r)]);
        setAnchor(i);
      }
    },
    [toggle],
  );
  const addMarked = () => {
    if (!marked.size) return;
    // in the order marked (a Shift range in the order shown), the marks a filter hides too
    const keys = [...marked];
    if (manual) setTyping(keys);
    else {
      onPicks(added(picks, keys));
      setMarked(new Set());
    }
  };
  const addTyped = (numbers: number[]) => {
    const keys = (typing ?? []).flatMap((k) => numbers.map((alp) => keyOf({ ...pickOf(k), alp })));
    onPicks(added(picks, keys));
    setTyping(null);
    setMarked(new Set());
  };
  const removeSelected = () => {
    if (!selected.size) return;
    onPicks(picks.filter((k) => !selected.has(k)));
    setSelected(new Set());
  };
  useHotkeys([
    ["F8", addMarked],
    ["Delete", removeSelected],
  ]);
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const move = e.key === "ArrowDown" ? 1 : e.key === "ArrowUp" ? -1 : 0;
    if (move) {
      e.preventDefault();
      const to = Math.min(Math.max(cursor + move, 0), rows.length - 1);
      setCursor(to);
      if (e.shiftKey) toggle(range(rows, anchor, to), true);
      else setAnchor(to);
    } else if (e.key === " ") {
      e.preventDefault();
      mark(cursor, false);
    }
  };
  return (
    <div className="fill-row sel-pick-step">
      <RulesSummary ev={props.ev} source={props.source} total={list?.total ?? 0} spec={props.spec} onEdit={props.onEdit} />
      <SourceTable
        ev={props.ev}
        title={props.source}
        table={table}
        marked={marked}
        picked={picked}
        cursor={cursor}
        filter={filter}
        loading={props.loading}
        truncated={!!list?.truncated}
        geneBankTrials={props.spec.source === "trials" ? props.spec.trials : []}
        onFilter={setFilter}
        onMark={mark}
        onKeyDown={onKeyDown}
        onSisters={() => toggle(sistersOf(all, marked), true)}
        hideTaken={hideTaken}
        onHideTaken={setHideTaken}
        onAdd={addMarked}
        target={props.spec.rules.target}
      />
      <TargetPanel
        ev={props.ev + 1}
        target={props.spec.rules.target}
        picks={picks}
        rows={byKey}
        copies={props.spec.rules.copies ?? 1}
        existing={props.existing}
        selected={selected}
        onSelect={(k, add) => setSelected(toggled(add ? selected : new Set(), [k]))}
        onRemove={removeSelected}
        onMove={(from, to) => onPicks(moved(picks, from, to))}
        onTakeOut={setTakeOut}
        onPreview={props.onPreview}
      />
      {takeOut && <RemovalModal ev={props.ev + 1} kis={props.spec.rules.target.trim()} parcs={takeOut} onClose={() => setTakeOut(null)} />}
      {typing && (
        <SubplotModal plots={typing.length} all={props.spec.rules.seed.mode === "all"} onAdd={addTyped} onClose={() => setTyping(null)} />
      )}
    </div>
  );
}
